import React from "react";
import { Formik } from "formik";
import {
    TextField,
    withStyles,
    Button,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress
} from "@material-ui/core";
import * as Yup from "yup";

const styles = theme => ({
    root: {
        width: 'auto',
        height: 'auto',
        padding: theme.spacing(2),
        margin: theme.spacing(2),
    },
    submit: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        backgroundColor: theme.palette.primary.main,
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
        width: "100%",
        marginTop: "10px"
    },
    formControl: {
        width: "100%"
    }

});

const AuthServiceForm = props => {
    const { classes, formSettings, initialValues, handleSubmit, translations } = props;

    return (
        <div className={classes.root}>
            <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                onSubmit={async (values, { setSubmitting }) => {
                    setSubmitting(true);
                    await handleSubmit(values);
                    setSubmitting(false);
                }}
                validationSchema={Yup.object().shape(formSettings.Validation)}
            >
                {formikProps => {
                    const { values, touched, errors, isValid, dirty, isSubmitting, handleChange, handleBlur, handleSubmit } = formikProps;
                    return (
                        <form onSubmit={handleSubmit}>
                            <Typography variant={"h6"}>{formSettings.FormTitle ? formSettings.FormTitle : null}</Typography>
                            <TextField
                                name="Name"
                                value={values.Name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.Name && touched.Name)}
                                placeholder={translations['auth_service_name'] || 'Authorization Service Name'}
                                label={translations['auth_service_name'] || 'Authorization Service Name'}
                                margin={"dense"}
                                fullWidth={true}
                            />
                            <TextField
                                name="BaseURL"
                                value={values.BaseURL}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.BaseURL && touched.BaseURL)}
                                placeholder={translations['auth_service_base_url'] || 'Authorization Service Base URL'}
                                label={translations['auth_service_base_url'] || 'Authorization Service Base URL'}
                                margin={"dense"}
                                fullWidth={true}
                            />
                            <FormControl id="active_select" className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["active"] || "Active"}</InputLabel>
                                <Select
                                    name="Active"
                                    value={values.Active}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!(errors.Active && touched.Active)}
                                    placeholder={translations["active"] || "Active"}
                                    label={translations["active"] || "Active"}
                                    margin={"dense"}
                                    fullWidth={true}
                                >
                                    <MenuItem value={true}>
                                        {translations['active'] || 'Active'}
                                    </MenuItem>
                                    <MenuItem value={false}>
                                        {translations['inactive'] || 'Inactive'}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl id="type_select" className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["type"] || "Type"}</InputLabel>
                                <Select
                                    name="Type"
                                    value={values.Type}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!(errors.Type && touched.Type)}
                                    placeholder={translations["type"] || "Type"}
                                    label={translations["type"] || "Type"}
                                    margin={"dense"}
                                    fullWidth={true}
                                >
                                    <MenuItem value={'openid'}>
                                        {translations['openid'] || 'OpenID'}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            {
                                values.Type === 'openid' && (
                                    <>
                                        <InputLabel shrink>{translations['select_openid_services'] || "Please select which services you wish to use"}</InputLabel>
                                        <TextField
                                            name="ClientID"
                                            value={values.ClientID}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.ClientID && touched.ClientID)}
                                            placeholder={translations['client_id'] || 'Client ID'}
                                            label={translations['client_id'] || 'Client ID'}
                                            margin={"dense"}
                                            fullWidth={true}
                                        />
                                        <TextField
                                            name="ClientSecret"
                                            value={values.ClientSecret}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.ClientSecret && touched.ClientSecret)}
                                            placeholder={translations['client_secret'] || 'Client Secret'}
                                            label={translations['client_secret'] || 'Client Secret'}
                                            margin={"dense"}
                                            fullWidth={true}
                                        />
                                        <TextField
                                            name="GrantTypes"
                                            value={values.GrantTypes}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.GrantTypes && touched.GrantTypes)}
                                            placeholder={translations['grant_types'] || 'Grant Types'}
                                            label={translations['grant_types'] || 'Grant Types'}
                                            margin={"dense"}
                                            fullWidth={true}
                                        />
                                        <TextField
                                            name="ResponseTypes"
                                            value={values.ResponseTypes}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.ResponseTypes && touched.ResponseTypes)}
                                            placeholder={translations['response_types'] || 'Response Types'}
                                            label={translations['response_types'] || 'Response Types'}
                                            margin={"dense"}
                                            fullWidth={true}
                                        />
                                        <TextField
                                            name="RedirectURIs"
                                            value={values.RedirectURIs}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.RedirectURIs && touched.RedirectURIs)}
                                            placeholder={translations['redirect_uris'] || 'Redirect URIs'}
                                            label={translations['redirect_uris'] || 'Redirect URIs'}
                                            margin={"dense"}
                                            fullWidth={true}
                                        />
                                        <FormControl className={classes.formControl} margin={"dense"}>
                                            <InputLabel shrink>{translations["auto_create_technical_user"] || "Auto create Technical User"}</InputLabel>
                                            <Select
                                                name="AutoCreateTechnicalUser"
                                                value={values.AutoCreateTechnicalUser}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={!!(errors.AutoCreateTechnicalUser && touched.AutoCreateTechnicalUser)}
                                                placeholder={translations["auto_create_technical_user"] || "Auto create Technical User"}
                                                label={translations["auto_create_technical_user"] || "Auto create Technical User"}
                                                margin={"dense"}
                                                fullWidth={true}
                                            >
                                                <MenuItem value={true}>
                                                    {translations['yes'] || 'Yes'}
                                                </MenuItem>
                                                <MenuItem value={false}>
                                                    {translations['no'] || 'No'}
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                        {
                                            values.AutoCreateTechnicalUser && (
                                                <>
                                                    <TextField
                                                        name="TechnicalUserNameFormat"
                                                        value={values.TechnicalUserNameFormat}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={!!(errors.TechnicalUserNameFormat && touched.TechnicalUserNameFormat)}
                                                        placeholder={translations['technical_username_format'] || 'Username format for technical users (use {FirstName}, {LastName}, and {UserID} as placeholders.)'}
                                                        label={translations['technical_username_format'] || 'Username format for technical users (use {FirstName}, {LastName}, and {UserID} as placeholders.)'}
                                                        margin={"dense"}
                                                        fullWidth={true}
                                                    />
                                                    <TextField
                                                        name="TechnicalUserDefaultFirstName"
                                                        value={values.TechnicalUserDefaultFirstName}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={!!(errors.TechnicalUserDefaultFirstName && touched.TechnicalUserDefaultFirstName)}
                                                        placeholder={translations['technical_user_default_first_name'] || 'Default first name for the technical user'}
                                                        label={translations['technical_user_default_first_name'] || 'Default first name for the technical user'}
                                                        margin={"dense"}
                                                        fullWidth={true}
                                                    />
                                                    <TextField
                                                        name="TechnicalUserDefaultLastName"
                                                        value={values.TechnicalUserDefaultLastName}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={!!(errors.TechnicalUserDefaultLastName && touched.TechnicalUserDefaultLastName)}
                                                        placeholder={translations['technical_user_default_last_name'] || 'Default last name for the technical user'}
                                                        label={translations['technical_user_default_last_name'] || 'Default last name for the technical user'}
                                                        margin={"dense"}
                                                        fullWidth={true}
                                                    />
                                                </>
                                            )
                                        }
                                    </>
                                )
                            }

                            <div>
                                {formSettings.ButtonSubmit ? (
                                    <Button
                                        className={classes.submit}
                                        variant="contained"
                                        color={formSettings.ButtonSubmit.Color}
                                        type="submit"
                                        style={{ color: isSubmitting || !(isValid && dirty) ? null : 'white' }}
                                        disabled={isSubmitting || !(isValid && dirty)}
                                    >
                                        {isSubmitting ? <CircularProgress size={24} /> : formSettings.ButtonSubmit.Title}
                                    </Button>
                                ) : null}
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default withStyles(styles, { withTheme: true })(AuthServiceForm);
